/**
 * Phase 105-Agent-B — Agent Earnings.
 *
 * Route: /dashboard/agent/earnings (RoleGate: agent only)
 *
 * Reads /api/agent/earnings and presents:
 *   • Date range filter (Today, 7d, 30d, All)
 *   • Summary tiles: pickups · deliveries · earnings · COD collected ·
 *     COD pending settlement
 *   • Ledger table: type · order · amount · status · created_at
 *   • CSV export of the visible rows (client-side blob download).
 *
 * Server-side scoping: agents see only their rows; admins may pass
 * `?agent_user_id=` (not exposed here).
 */
import { useEffect, useState } from "react";
import { Loader2, RefreshCw, IndianRupee, Download, Bike, PackageCheck, Banknote, Wallet, Calendar } from "lucide-react";
import { toast } from "sonner";
import RoleGate from "../../components/RoleGate";
import api from "../../services/api";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "7d",    label: "Last 7 days" },
  { key: "30d",   label: "Last 30 days" },
  { key: "all",   label: "All time" },
];

function startOfDayUTC(d) {
  const x = new Date(d);
  x.setUTCHours(0, 0, 0, 0);
  return x;
}
function rangeBounds(key) {
  const now = new Date();
  if (key === "today") {
    return { from: startOfDayUTC(now).toISOString(), to: undefined };
  }
  if (key === "7d") {
    const f = new Date(now); f.setUTCDate(f.getUTCDate() - 7);
    return { from: startOfDayUTC(f).toISOString(), to: undefined };
  }
  if (key === "30d") {
    const f = new Date(now); f.setUTCDate(f.getUTCDate() - 30);
    return { from: startOfDayUTC(f).toISOString(), to: undefined };
  }
  return { from: undefined, to: undefined };
}

function csvCell(v) {
  if (v == null) return "";
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
}

function rowsToCsv(rows) {
  const header = ["created_at", "earning_type", "tracking_id", "amount", "currency", "method", "status", "reconciliation_status"];
  const out = [header.join(",")];
  for (const r of rows) {
    out.push([
      r.created_at, r.earning_type, r.tracking_id || r.order_id,
      r.amount, r.currency, r.method, r.status, r.reconciliation_status,
    ].map(csvCell).join(","));
  }
  return out.join("\n");
}

function Tile({ icon: Icon, label, value, tone = "slate", testid }) {
  const toneMap = {
    blue:    "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber:   "bg-amber-50 text-amber-700",
    slate:   "bg-slate-100 text-slate-700",
    rose:    "bg-rose-50 text-rose-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col gap-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <p className="text-2xl font-bold font-['Outfit'] text-slate-900 tracking-tight">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

function Inner() {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { from, to } = rangeBounds(range);
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const { data } = await api.get("/agent/earnings", { params });
      setData(data);
      setError(null);
    } catch (e) {
      const code = e?.response?.status;
      setError(code === 403 ? "You don't have access to earnings." : "Couldn't load earnings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range]);

  const summary = data?.summary || {};
  // Hide operational marker rows from the ledger UI — they're counter-only
  // (amount=0) and only exist to keep the summary tiles accurate.
  const rows = (data?.rows || []).filter((r) => r?.leg !== "operational");

  const totalEarnings = summary.total_earnings || 0;
  const codCollected = summary.cod_collected_amount || 0;
  const codPending = summary.cod_pending_settlement || 0;

  const downloadCsv = () => {
    if (!rows.length) { toast.info("No rows to export in this range."); return; }
    const csv = rowsToCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pickngo-earnings-${range}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${rows.length} row${rows.length === 1 ? "" : "s"}.`);
  };

  const fmtRow = (r) => {
    let ts = r.created_at;
    try { ts = new Date(r.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { /* noop */ }
    const labelMap = {
      pickup: "Pickup",
      delivery: "Delivery",
      cod_received: "COD",
      payout: "Payout",
      incentive: "Incentive",
    };
    const tone = r.earning_type === "cod_received"
      ? (r.reconciliation_status === "settled" ? "text-emerald-700" : "text-amber-700")
      : r.earning_type === "delivery" || r.earning_type === "pickup"
        ? "text-blue-700"
        : "text-slate-600";
    return { ts, tone, label: labelMap[r.earning_type] || r.earning_type };
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agent-earnings-page">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Top bar */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">My earnings</h1>
            <p className="text-sm text-slate-500 mt-0.5">Pickups · deliveries · COD · reconciliation</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="earnings-range-tabs">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setRange(r.key)}
                  className={`px-3 py-2 text-xs font-semibold transition ${
                    range === r.key
                      ? "bg-[#FF6B00] text-white"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`earnings-range-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <button
              onClick={load}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700"
              data-testid="earnings-refresh-btn"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Refresh
            </button>
            <button
              onClick={downloadCsv}
              disabled={!rows.length}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="earnings-export-csv-btn"
              title={!rows.length ? "No rows to export" : `Export ${rows.length} rows`}
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>
        </div>

        {/* Tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="earnings-tiles">
          <Tile icon={Bike}         label="Pickups completed"     value={summary.total_pickups || 0}        tone="blue"    testid="tile-pickups" />
          <Tile icon={PackageCheck} label="Deliveries completed" value={summary.total_deliveries || 0}     tone="emerald" testid="tile-deliveries" />
          <Tile icon={IndianRupee}  label="Total earnings"        value={`₹${totalEarnings}`}              tone="emerald" testid="tile-earnings" />
          <Tile icon={Banknote}     label="COD collected"         value={`₹${codCollected}`}               tone="amber"   testid="tile-cod" />
        </div>

        {codPending > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-center gap-3" data-testid="earnings-cod-pending-banner">
            <Wallet className="h-5 w-5 text-amber-600 shrink-0" />
            <div className="flex-1 text-sm text-amber-900">
              <span className="font-bold">₹{codPending}</span> in COD is pending hub reconciliation. Deposit cash at your hub close-out to clear this.
            </div>
          </div>
        )}

        {/* Ledger */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="earnings-ledger">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-400" />
              <h2 className="font-['Outfit'] text-sm font-bold text-slate-800">
                Ledger · {rows.length} row{rows.length === 1 ? "" : "s"}
              </h2>
            </div>
            <p className="text-[11px] text-slate-400">Newest first</p>
          </div>

          {loading ? (
            <div className="p-10 text-center text-slate-500 flex flex-col items-center gap-3" data-testid="earnings-loading">
              <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
              Loading earnings…
            </div>
          ) : error ? (
            <div className="p-10 text-center" data-testid="earnings-error">
              <p className="text-sm font-semibold text-rose-600">{error}</p>
              <button onClick={load} className="mt-3 px-4 py-2 rounded-xl bg-slate-100 text-xs font-bold">Try again</button>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center" data-testid="earnings-empty">
              <p className="text-sm font-semibold text-slate-700">No earnings in this range yet.</p>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">Complete a pickup or delivery to start seeing rows here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="earnings-table-wrap">
              <table className="w-full text-sm" data-testid="earnings-table">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2.5">When</th>
                    <th className="text-left px-4 py-2.5">Type</th>
                    <th className="text-left px-4 py-2.5">Order</th>
                    <th className="text-left px-4 py-2.5">Method</th>
                    <th className="text-right px-4 py-2.5">Amount</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const f = fmtRow(r);
                    return (
                      <tr key={r.entry_id || i} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`earnings-row-${r.entry_id || i}`}>
                        <td className="px-4 py-2 text-xs text-slate-600">{f.ts}</td>
                        <td className={`px-4 py-2 text-xs font-semibold ${f.tone}`}>{f.label}</td>
                        <td className="px-4 py-2 text-xs font-mono text-slate-700">{r.tracking_id || r.order_id || "—"}</td>
                        <td className="px-4 py-2 text-xs text-slate-500">{r.method || "—"}</td>
                        <td className="px-4 py-2 text-xs text-slate-900 font-semibold text-right tabular-nums">
                          {r.currency === "INR" ? "₹" : ""}{r.amount ?? 0}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                            r.reconciliation_status === "settled"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : r.status === "pending_settlement"
                                ? "bg-amber-50 text-amber-700 border border-amber-200"
                                : "bg-slate-100 text-slate-700"
                          }`}>
                            {r.reconciliation_status || r.status || "—"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AgentEarnings() {
  return (
    <RoleGate allow={["agent", "admin"]} fallbackTo="/dashboard">
      <Inner />
    </RoleGate>
  );
}
